import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowDownCircle,
    ArrowLeft,
    ArrowUpCircle,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    IndianRupee,
    ListFilter,
    Loader2,
    ReceiptText,
    RefreshCw,
    Scale,
} from "lucide-react";
import { toast } from "sonner";
import config from "../config";

function localDate() {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 10);
}

const TODAY = localDate();
const EMPTY_SUMMARY = { totalDebit: 0, totalCredit: 0, netAmount: 0, totalRecords: 0 };

async function readJson(response) {
    const text = await response.text();
    if (!text.trim()) throw new Error(`Server returned an empty response. HTTP ${response.status}.`);
    try { return JSON.parse(text); }
    catch {
        console.error("Invalid ledger response:", text);
        throw new Error("The server returned an invalid response. Check the PHP error log.");
    }
}

export default function Ledger({ endpoint = `${config.API_URL}/ledger.php` }) {
    const navigate = useNavigate();
    const [inputs, setInputs] = useState({ from: TODAY, to: TODAY });
    const [filters, setFilters] = useState({ from: TODAY, to: TODAY });
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [pagination, setPagination] = useState({ page: 1, perPage: 25, totalPages: 1 });
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const loadLedger = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const params = new URLSearchParams({ from: filters.from, to: filters.to, page: String(page) });
            const response = await fetch(`${endpoint}?${params.toString()}`, {
                method: "GET",
                credentials: "include",
                cache: "no-store",
                headers: { Accept: "application/json" },
            });
            const result = await readJson(response);

            if (!response.ok || !result.success) {
                throw new Error(result.message || "Unable to load the ledger.");
            }

            setTransactions(Array.isArray(result.transactions) ? result.transactions : []);
            setSummary(result.summary || EMPTY_SUMMARY);
            setPagination(result.pagination || { page: 1, perPage: 25, totalPages: 1 });
        } catch (loadError) {
            const message = loadError instanceof Error ? loadError.message : "Unable to load the ledger.";
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    }, [endpoint, filters, page]);

    useEffect(() => { loadLedger(); }, [loadLedger]);

    const handleFilter = (event) => {
        event.preventDefault();
        if (!inputs.from || !inputs.to) {
            toast.error("Please select both dates.");
            return;
        }
        if (inputs.from > inputs.to) {
            toast.error("From Date cannot be later than To Date.");
            return;
        }
        setPage(1);
        setFilters(inputs);
    };

    const rangeLabel = useMemo(
        () => filters.from === filters.to ? formatFilterDate(filters.from) : `${formatFilterDate(filters.from)} – ${formatFilterDate(filters.to)}`,
        [filters]
    );

    return (
        <main className="relative min-h-[calc(100vh-65px)] overflow-hidden px-4 py-8 sm:px-6 sm:py-12">
            <div className="pointer-events-none absolute inset-0 flex justify-center"><div className="mt-4 h-[560px] w-[560px] rounded-full bg-gradient-to-tr from-[#3BC7FF]/15 to-[#7B61FF]/20 blur-3xl" /></div>

            <div className="relative mx-auto w-full max-w-6xl fade-in-up">
                <div className="mb-5 flex items-center justify-between gap-3">
                    <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground transition-colors"><ArrowLeft className="h-4 w-4" />Back</button>
                    <button type="button" onClick={loadLedger} disabled={loading} className="inline-flex items-center gap-2 rounded-full glass-card px-3.5 py-2 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh</button>
                </div>

                <section className="glass-card rounded-3xl border border-border/70 overflow-hidden shadow-xl">
                    <header className="relative border-b border-border/70 p-6 sm:p-8">
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/[0.06] to-violet-500/[0.06]" />
                        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                            <div className="flex items-start gap-4">
                                <div className="h-14 w-14 shrink-0 rounded-2xl btn-gradient flex items-center justify-center text-white shadow-lg"><ReceiptText className="h-7 w-7" /></div>
                                <div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Account Transactions</p><h1 className="mt-1 text-2xl sm:text-3xl font-display font-bold">My <span className="text-gradient-brand">Ledger</span></h1><p className="mt-1.5 text-sm text-muted-foreground">Review wallet debits, credits, and transaction references.</p></div>
                            </div>

                            <form onSubmit={handleFilter} className="grid grid-cols-2 gap-3 sm:flex sm:items-end">
                                <DateField label="From Date" value={inputs.from} max={TODAY} onChange={(value) => setInputs((current) => ({ ...current, from: value }))} />
                                <DateField label="To Date" value={inputs.to} max={TODAY} onChange={(value) => setInputs((current) => ({ ...current, to: value }))} />
                                <button type="submit" disabled={loading} className="col-span-2 h-11 rounded-full btn-gradient px-5 text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60"><ListFilter className="h-4 w-4" />Show Ledger</button>
                            </form>
                        </div>
                    </header>

                    <div className="p-6 sm:p-8">
                        <div className="mb-3 flex items-center justify-between gap-3"><p className="text-xs font-medium text-muted-foreground">Showing: <span className="text-foreground">{rangeLabel}</span></p><p className="text-xs text-muted-foreground">{summary.totalRecords} transaction{summary.totalRecords === 1 ? "" : "s"}</p></div>

                        <div className="grid gap-3 sm:grid-cols-3">
                            <SummaryCard icon={ArrowDownCircle} label="Total Debit" value={summary.totalDebit} color="red" />
                            <SummaryCard icon={ArrowUpCircle} label="Total Credit" value={summary.totalCredit} color="emerald" />
                            <SummaryCard icon={Scale} label="Net Amount" value={summary.netAmount} color={summary.netAmount < 0 ? "red" : "blue"} />
                        </div>

                        {error && <div className="mt-5 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</div>}

                        <div className="mt-6 overflow-hidden rounded-2xl border border-border/70">
                            {loading ? (
                                <div className="flex min-h-64 items-center justify-center gap-3 text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-primary" />Loading transactions…</div>
                            ) : transactions.length === 0 ? (
                                <div className="flex min-h-64 flex-col items-center justify-center px-4 text-center"><div className="h-14 w-14 rounded-2xl bg-foreground/[0.05] flex items-center justify-center"><ReceiptText className="h-6 w-6 text-muted-foreground" /></div><h3 className="mt-4 font-display font-semibold">No transactions found</h3><p className="mt-1 text-sm text-muted-foreground">Try selecting a different date range.</p></div>
                            ) : (
                                <>
                                    <div className="hidden overflow-x-auto md:block">
                                        <table className="w-full text-sm">
                                            <thead className="bg-foreground/[0.04] text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground"><tr><th className="px-5 py-3.5">ID</th><th className="px-5 py-3.5">Date</th><th className="px-5 py-3.5">Reference</th><th className="px-5 py-3.5 text-right">Debit</th><th className="px-5 py-3.5 text-right">Credit</th></tr></thead>
                                            <tbody className="divide-y divide-border/60">{transactions.map((item) => <tr key={item.id} className="hover:bg-foreground/[0.025] transition-colors"><td className="px-5 py-4 font-mono text-xs text-muted-foreground">#{item.id}</td><td className="px-5 py-4 whitespace-nowrap">{item.date}</td><td className="px-5 py-4 font-mono text-xs">{item.refno || "—"}</td><td className="px-5 py-4 text-right font-semibold text-red-500">{item.debit > 0 ? `₹ ${money(item.debit)}` : "—"}</td><td className="px-5 py-4 text-right font-semibold text-emerald-600">{item.credit > 0 ? `₹ ${money(item.credit)}` : "—"}</td></tr>)}</tbody>
                                        </table>
                                    </div>

                                    <div className="divide-y divide-border/60 md:hidden">{transactions.map((item) => <TransactionCard key={item.id} item={item} />)}</div>
                                </>
                            )}
                        </div>

                        {!loading && pagination.totalPages > 1 && (
                            <div className="mt-5 flex items-center justify-between gap-3">
                                <button type="button" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} className="h-10 rounded-full border border-border px-4 text-sm font-medium flex items-center gap-1.5 hover:bg-foreground/[0.04] disabled:opacity-40"><ChevronLeft className="h-4 w-4" />Previous</button>
                                <span className="text-xs text-muted-foreground">Page <strong className="text-foreground">{pagination.page}</strong> of {pagination.totalPages}</span>
                                <button type="button" disabled={page >= pagination.totalPages} onClick={() => setPage((current) => current + 1)} className="h-10 rounded-full border border-border px-4 text-sm font-medium flex items-center gap-1.5 hover:bg-foreground/[0.04] disabled:opacity-40">Next<ChevronRight className="h-4 w-4" /></button>
                            </div>
                        )}
                    </div>
                </section>
                <p className="mt-4 text-center text-[11px] uppercase tracking-[0.26em] text-muted-foreground">Secured by Smart Solutions</p>
            </div>
        </main>
    );
}

function DateField({ label, value, max, onChange }) {
    return <label className="block"><span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</span><div className="relative mt-1.5"><CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><input type="date" value={value} max={max} onChange={(event) => onChange(event.target.value)} className="h-11 w-full rounded-xl border border-border bg-background/60 pl-9 pr-2 text-xs outline-none focus:border-primary focus:ring-2 focus:ring-primary/25 sm:w-[150px]" /></div></label>;
}

function SummaryCard({ icon: Icon, label, value, color }) {
    const styles = { red: "bg-red-500/10 text-red-500", emerald: "bg-emerald-500/10 text-emerald-600", blue: "bg-blue-500/10 text-blue-500" };
    return <div className="rounded-2xl border border-border/70 bg-background/40 p-4 flex items-center gap-3"><span className={`h-10 w-10 rounded-xl flex items-center justify-center ${styles[color]}`}><Icon className="h-5 w-5" /></span><div><p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p><p className="mt-0.5 text-lg font-display font-bold flex items-center"><IndianRupee className="h-4 w-4" />{money(value)}</p></div></div>;
}

function TransactionCard({ item }) {
    const isCredit = Number(item.credit) > 0;
    const amount = isCredit ? item.credit : item.debit;
    return <div className="p-4 flex items-start gap-3"><span className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center ${isCredit ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-500"}`}>{isCredit ? <ArrowUpCircle className="h-5 w-5" /> : <ArrowDownCircle className="h-5 w-5" />}</span><div className="min-w-0 flex-1"><div className="flex items-start justify-between gap-3"><div><p className="font-mono text-xs font-semibold truncate">{item.refno || `Transaction #${item.id}`}</p><p className="mt-1 text-[11px] text-muted-foreground">{item.date} · ID #{item.id}</p></div><p className={`whitespace-nowrap text-sm font-bold ${isCredit ? "text-emerald-600" : "text-red-500"}`}>{isCredit ? "+" : "−"} ₹ {money(amount)}</p></div></div></div>;
}

function money(value) {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
}

function formatFilterDate(value) {
    if (!value) return "—";
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
}
